import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { discoveryGuest, type GuestForm } from '../api/discovery'
import { DiscoverySchemaForm } from '../components/DiscoverySchemaForm'
import type { JsonSchema, UiSchema } from '../lib/discoverySchema'

/**
 * Public guest surface (`#/d/:token`) — no Firebase. The link proves you got
 * the capability token; the OTP to a thread-party inbox proves it's really
 * you. Never shows the audience list, the owner, or other answers.
 */
export function GuestDiscoveryPage() {
  const { token = '' } = useParams()

  const [form, setForm] = useState<GuestForm | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [devCode, setDevCode] = useState('')
  const [session, setSession] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('Missing discovery link token.')
      return
    }
    void discoveryGuest
      .resolve(token)
      .then((r) => setForm(r.form))
      .catch(() => setError('This discovery link is invalid or has expired.'))
  }, [token])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shell">
      <header className="top compact">
        <div className="brand">
          <span className="mark">BeyondQuota</span>
          <h1>{form?.title ?? 'Discovery'}</h1>
        </div>
        <p className="lede tight">
          {done
            ? 'Thanks — your answers are in.'
            : 'A quick structured form from your BeyondQuota thread. Verify your email to answer.'}
        </p>
      </header>

      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}

      {!form && !error ? <p className="empty">Loading…</p> : null}

      {form && !session && !done ? (
        <form
          className="k-inline-form preview-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (!codeSent) {
              void run(async () => {
                const r = await discoveryGuest.requestOtp(token, email)
                setCodeSent(true)
                if (r.dev_otp_code) {
                  setDevCode(r.dev_otp_code)
                  setOtp(r.dev_otp_code)
                }
              })
            } else {
              void run(async () => {
                const s = await discoveryGuest.openSession(token, email, otp)
                setSession(s.session_token)
              })
            }
          }}
        >
          <label className="ctl grow">
            <span>Your email (must be on the thread)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              spellCheck={false}
              disabled={codeSent}
            />
          </label>
          {codeSent ? (
            <label className="ctl">
              <span>Code from your inbox</span>
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            </label>
          ) : null}
          <button type="submit" className="ghost" disabled={busy}>
            {busy ? '…' : codeSent ? 'Verify' : 'Send code'}
          </button>
          {devCode ? (
            <span className="pill muted" title="Beta dogfood — emailed in prod">
              beta code {devCode}
            </span>
          ) : null}
        </form>
      ) : null}

      {form && session && !done ? (
        <DiscoverySchemaForm
          schema={form.json_schema as JsonSchema}
          uiSchema={(form.ui_schema ?? {}) as UiSchema}
          busy={busy}
          onSubmit={(answers) =>
            void run(async () => {
              await discoveryGuest.submit(session, answers)
              setDone(true)
            })
          }
        />
      ) : null}

      {done ? <p className="empty">Submitted — you can close this tab.</p> : null}
    </div>
  )
}
